import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, Clock, Calendar, UserCheck, ShieldAlert } from "lucide-react";

const CONTROLLED = [["first_interview", "First Interview"], ["final_interview", "Final Interview"]];
const STEPS = ["Meeting", "Recruiter", "Pre-Screening", "Date & Time", "Review"];

export default function ScheduleInterview() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [step, setStep] = useState(0);
  const [meetingKey, setMeetingKey] = useState("first_interview");
  const [recruiters, setRecruiters] = useState([]);
  const [recruiter, setRecruiter] = useState(null);
  const [form, setForm] = useState(null);
  const [candidate, setCandidate] = useState({ first_name: "", last_name: "", email: "", mobile: "", city: "", state: "", zip: "", address: "", address2: "" });
  const [answers, setAnswers] = useState({});
  const [submission, setSubmission] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/interviews/access").then((r) => setAccess(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { api.get("/interviews/prescreen-form").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const loadRecruiters = async (key) => {
    const slug = params.get("recruiter") || "";
    const { data } = await api.get("/interviews/eligible-recruiters", { params: { meeting_key: key, slug } });
    setRecruiters(data.items);
    if (data.auto_selected) { setRecruiter(data.auto_selected); return true; }
    return false;
  };

  const startMeeting = async () => {
    try {
      const auto = await loadRecruiters(meetingKey);
      const needForm = (recruiter?.required_form || (auto ? recruiter?.required_form : "")) === "pre_screening";
      setStep(auto ? (needForm ? 2 : 3) : 1);
    } catch (e) { toast.error(apiError(e)); }
  };

  const chooseRecruiter = (r) => { setRecruiter(r); setStep(r.required_form === "pre_screening" ? 2 : 3); };

  const submitPrescreen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/interviews/prescreen/submit", { recruiter_user_id: recruiter.user_id, meeting_key: meetingKey, candidate, answers });
      setSubmission(data);
      toast.success("Pre-Screening submitted");
      setStep(3);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const loadSlots = async (d) => {
    setDate(d); setSlot("");
    try {
      const { data } = await api.get("/interviews/slots", { params: { recruiter_user_id: recruiter.user_id, appointment_type_id: recruiter.appointment_type_id, date: d, tz: Intl.DateTimeFormat().resolvedOptions().timeZone } });
      setSlots(data.slots);
    } catch (e) { toast.error(apiError(e)); setSlots([]); }
  };

  const book = async () => {
    setBusy(true);
    try {
      const body = { recruiter_user_id: recruiter.user_id, appointment_type_id: recruiter.appointment_type_id, start: slot };
      if (submission) { body.submission_id = submission.submission_id; body.portal_activation_link = submission.activation_link; }
      const { data } = await api.post("/interviews/book", body);
      toast.success("Interview scheduled");
      navigate(`/people/detail/${data.person_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!access) return <p className="text-slate-400 py-16 text-center">Loading…</p>;
  if (!access.can_book_controlled) return (
    <div className="max-w-lg mx-auto text-center py-16" data-testid="interview-no-access">
      <ShieldAlert className="w-10 h-10 mx-auto text-amber-400 mb-3" />
      <h2 className="font-display text-lg font-bold text-slate-800">Approval required</h2>
      <p className="text-sm text-slate-500 mt-2">{access.is_outside_recruiter
        ? "Your Outside Recruiter access is pending approval by a recruiting manager. You'll be able to schedule interviews once approved."
        : "You do not have permission to schedule controlled interviews."}</p>
    </div>
  );

  const requiresForm = recruiter?.required_form === "pre_screening";
  const visibleSteps = requiresForm ? STEPS : STEPS.filter((s) => s !== "Pre-Screening");

  return (
    <div className="max-w-3xl">
      <PageHeader title="Schedule Candidate Interview" description="Refer and schedule a candidate with an internal recruiter." />
      {access.is_outside_recruiter && (
        <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-600" data-testid="interview-referrer">
          Referred By <b>{access.referrer.name}</b> · {access.referrer.email}
        </div>
      )}
      <div className="flex items-center gap-2 mb-6 text-xs overflow-x-auto">
        {visibleSteps.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 shrink-0 ${i <= (requiresForm ? step : (step > 2 ? step - 1 : step)) ? "text-brand" : "text-slate-400"}`}>
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${i <= step ? "bg-brand text-white" : "bg-slate-200"}`}>{i + 1}</span>{s}
            {i < visibleSteps.length - 1 && <ChevronRight className="w-3.5 h-3.5" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Panel title="Select Meeting Type">
          <div className="grid gap-2">
            {CONTROLLED.map(([k, l]) => (
              <label key={k} className={`flex items-center justify-between rounded-lg border px-4 py-3 cursor-pointer ${meetingKey === k ? "border-brand bg-brand/5" : "border-slate-200"}`} data-testid={`meeting-${k}`}>
                <span className="text-sm font-medium text-slate-700">{l}</span>
                <input type="radio" checked={meetingKey === k} onChange={() => setMeetingKey(k)} />
              </label>
            ))}
          </div>
          <Button className="mt-4 bg-brand hover:bg-brand-strong" onClick={startMeeting} data-testid="meeting-next">Continue</Button>
        </Panel>
      )}

      {step === 1 && (
        <Panel title="Interview With">
          {recruiters.length === 0 ? <p className="text-sm text-slate-400">No eligible recruiters have adopted this meeting yet.</p> : (
            <div className="grid gap-2" data-testid="recruiter-list">
              {recruiters.map((r) => (
                <button key={r.user_id} onClick={() => chooseRecruiter(r)} data-testid={`recruiter-${r.user_id}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 text-left hover:border-brand hover:bg-brand/5">
                  <UserCheck className="w-5 h-5 text-brand" />
                  <div><p className="text-sm font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-400">{r.title} · {r.duration_min} min</p></div>
                </button>
              ))}
            </div>
          )}
        </Panel>
      )}

      {step === 2 && requiresForm && form && (
        <Panel title="Pre-Screening Interview">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-600 mb-2">Candidate Information</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Fld label="First Name *"><Input value={candidate.first_name} onChange={(e) => setCandidate({ ...candidate, first_name: e.target.value })} data-testid="cand-first" /></Fld>
              <Fld label="Last Name *"><Input value={candidate.last_name} onChange={(e) => setCandidate({ ...candidate, last_name: e.target.value })} data-testid="cand-last" /></Fld>
              <Fld label="Email *"><Input value={candidate.email} onChange={(e) => setCandidate({ ...candidate, email: e.target.value })} data-testid="cand-email" /></Fld>
              <Fld label="Mobile Phone *"><Input value={candidate.mobile} onChange={(e) => setCandidate({ ...candidate, mobile: e.target.value })} data-testid="cand-mobile" /></Fld>
              <Fld label="City *"><Input value={candidate.city} onChange={(e) => setCandidate({ ...candidate, city: e.target.value })} data-testid="cand-city" /></Fld>
              <Fld label="State *"><Input value={candidate.state} onChange={(e) => setCandidate({ ...candidate, state: e.target.value })} data-testid="cand-state" /></Fld>
              <Fld label="ZIP Code *"><Input value={candidate.zip} onChange={(e) => setCandidate({ ...candidate, zip: e.target.value })} data-testid="cand-zip" /></Fld>
              <Fld label="Street Address"><Input value={candidate.address} onChange={(e) => setCandidate({ ...candidate, address: e.target.value })} data-testid="cand-address" /></Fld>
              <Fld label="Address 2"><Input value={candidate.address2} onChange={(e) => setCandidate({ ...candidate, address2: e.target.value })} data-testid="cand-address2" /></Fld>
            </div>
          </div>
          {form.sections.map((sec) => (
            <div key={sec.title} className="mt-4">
              <p className="text-xs font-semibold text-brand uppercase tracking-wide mb-2">{sec.title}</p>
              <div className="space-y-3">
                {sec.questions.filter((q) => !q.show_if || q.show_if[Object.keys(q.show_if)[0]] === answers[Object.keys(q.show_if)[0]]).map((q) => (
                  <Fld key={q.key} label={q.label + (q.required ? " *" : "")}>
                    <QInput q={q} value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
                  </Fld>
                ))}
              </div>
            </div>
          ))}
          <Button className="mt-4 bg-brand hover:bg-brand-strong" onClick={submitPrescreen} disabled={busy} data-testid="prescreen-submit">{busy ? "Submitting…" : "Submit Pre-Screening & Continue"}</Button>
        </Panel>
      )}

      {step === 3 && (
        <Panel title="Select Date & Time">
          <Fld label="Date"><Input type="date" value={date} onChange={(e) => loadSlots(e.target.value)} className="w-52" data-testid="interview-date" /></Fld>
          {date && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="interview-slots">
              {slots.length === 0 ? <p className="text-sm text-slate-400 col-span-full">No available times on this date.</p> :
                slots.map((s) => (
                  <button key={s.start} onClick={() => setSlot(s.start)} data-testid={`slot-${s.start}`}
                    className={`rounded-lg border px-2 py-2 text-sm ${slot === s.start ? "border-brand bg-brand text-white" : "border-slate-200 hover:border-brand"}`}>{s.label}</button>
                ))}
            </div>
          )}
          <Button className="mt-4 bg-brand hover:bg-brand-strong" onClick={() => setStep(4)} disabled={!slot} data-testid="datetime-next">Continue</Button>
        </Panel>
      )}

      {step === 4 && (
        <Panel title="Review & Schedule">
          <div className="space-y-1.5 text-sm">
            <Row label="Meeting" value={CONTROLLED.find((c) => c[0] === meetingKey)?.[1]} />
            <Row label="With" value={recruiter?.name} />
            {submission && <Row label="Candidate" value={`${candidate.first_name} ${candidate.last_name}`} />}
            <Row label="When" value={new Date(slot).toLocaleString()} />
          </div>
          <Button className="mt-4 bg-brand hover:bg-brand-strong" onClick={book} disabled={busy} data-testid="interview-confirm">{busy ? "Scheduling…" : "Schedule Candidate"}</Button>
        </Panel>
      )}
    </div>
  );
}

function Panel({ title, children }) { return <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5"><h3 className="font-semibold text-slate-800 mb-3">{title}</h3>{children}</div>; }
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>; }
function Row({ label, value }) { return <div className="flex justify-between gap-4"><span className="text-slate-400">{label}</span><span className="text-slate-700 font-medium text-right">{value || "—"}</span></div>; }

function QInput({ q, value, onChange }) {
  if (q.type === "long") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}`} />;
  if (q.type === "short") return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}`} />;
  if (q.type === "yesno") return (
    <Select value={value || ""} onValueChange={onChange}><SelectTrigger data-testid={`q-${q.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white"><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select>);
  if (q.type === "select") return (
    <Select value={value || ""} onValueChange={onChange}><SelectTrigger data-testid={`q-${q.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white">{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>);
  if (q.type === "multi") return (
    <div className="flex flex-wrap gap-2">{q.options.map((o) => {
      const arr = Array.isArray(value) ? value : [];
      const on = arr.includes(o);
      return <button key={o} type="button" onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
        className={`text-xs rounded-full px-3 py-1 border ${on ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`} data-testid={`q-${q.key}-${o}`}>{o}</button>;
    })}</div>);
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}
